import { Injectable } from '@angular/core';
import { CategoryNodeItem, SecondLevelCategoryNode, TopLevelCategoryNode } from '@libs/shop/models';
import { NavItemModel} from '@libs/shared/models';

@Injectable({ providedIn: 'root' })
export class CategoryNodeService {
  private readonly noCategoryTitle: string = 'Produkte ohne Kategorie';
  private readonly noCategoryId: string = 'noCategoryId';

  public createCategoryNodes<T extends CategoryNodeItem>(items: T[]): TopLevelCategoryNode<T>[] {
    if (!items.length) {
      return [];
    }

    const nodes: TopLevelCategoryNode<T>[] = [];
    const topLevelNodes: NavItemModel[] = [];
    const topLevelIds: string[] = [];

    const secondLevelIdCartNodeMap: Record<string, SecondLevelCategoryNode<T>> = {};
    const topLevelSecondLevelMap: Record<string, Set<string>> = {};
    const itemsWithoutCategory: T[] = [];

    for (const item of items) {
      // the categories are sorted in order
      const topLevel: NavItemModel | undefined = item.productData?.categories[0];
      const secondLevel: NavItemModel | undefined = item.productData?.categories[1];

      let secondLevelId: string = secondLevel?.id?.toString() ?? '';
      let topLevelId: string = '';

      if (!topLevel?.id) {
        itemsWithoutCategory.push(item);

        continue;
      }

      topLevelId = topLevel.id.toString();

      if (!secondLevelId) {
        secondLevelId = topLevelId + '_empty';
      }

      if (!topLevelIds.includes(topLevelId)) {
        topLevelIds.push(topLevelId);
        topLevelNodes.push(topLevel);
      }

      if (!topLevelSecondLevelMap[topLevelId]) {
        topLevelSecondLevelMap[topLevelId] = new Set([secondLevelId]);
      } else if (!topLevelSecondLevelMap[topLevelId].has(secondLevelId)) {
        topLevelSecondLevelMap[topLevelId].add(secondLevelId);
      }

      if (secondLevelIdCartNodeMap[secondLevelId]) {
        secondLevelIdCartNodeMap[secondLevelId].items.push(item);
      } else {
        secondLevelIdCartNodeMap[secondLevelId] = {
          label: secondLevel?.label ?? '',
          items: [item],
          id: secondLevelId,
        };
      }
    }

    for (const category of topLevelNodes) {
      // this cannot happen because this is filtered above
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const categoryId: string = category.id!.toString();
      const secondLevelsIds: string[] = Array.from(topLevelSecondLevelMap[categoryId]);
      const secondLevels: SecondLevelCategoryNode<T>[] = [];

      for (const id of secondLevelsIds) {
        const secondLevelNodes: SecondLevelCategoryNode<T> = secondLevelIdCartNodeMap[id];

        secondLevelNodes.items = secondLevelNodes.items.sort((a, b) => {
          const nameA: string | undefined = a.productData?.details.name;
          const nameB: string | undefined = b.productData?.details.name;

          return nameA && nameB ? nameA.localeCompare(nameB) : 0;
        });

        secondLevels.push(secondLevelNodes);
      }

      nodes.push({
        id: categoryId,
        label: category.label ?? '',
        count: secondLevels.reduce((a, b) => a + b.items.length, 0),
        children: secondLevels,
      });
    }

    if (itemsWithoutCategory.length > 0) {
      nodes.push({
        id: this.noCategoryId,
        label: this.noCategoryTitle,
        count: itemsWithoutCategory.length,
        children: [
          {
            label: '',
            id: this.noCategoryId,
            items: itemsWithoutCategory,
          },
        ],
      });
    }

    return nodes;
  }
}
